import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCents, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Pill, Loader, Button, EmptyState, Modal } from "@/components/ui/Primitives";
import LeaseGenerator from "@/components/LeaseGenerator";
import { AutoChips } from "@/pages/Auto";
import AutoCorrectness from "@/components/AutoCorrectness";
import { ArrowLeft, FileText, FileSignature, Calendar, DollarSign, User, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function LeaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [lease, setLease] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [genOpen, setGenOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await api.get("/leases");
      const found = (list.data || []).find((l) => l.lease_id === id);
      setLease(found);
      try {
        const d = await api.get(`/leases/${id}/document`);
        setDoc(d.data);
      } catch { /* document optional — some drafts don't have one yet */ }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const sign = async () => {
    setSigning(true);
    try {
      await api.post(`/leases/${id}/sign`);
      toast.success("Lease signed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign lease");
    } finally { setSigning(false); }
  };

  if (loading) return <Loader/>;
  if (!lease) return <EmptyState icon={FileText} title="Lease not found"/>;

  // Backend returns nested doc objects from both POST + GET. Normalize.
  const docNode = doc?.document ?? doc;
  const docText = typeof docNode === "string"
    ? docNode
    : (docNode?.body_md || docNode?.document_text || null);

  return (
    <div>
      <Link to="/leases" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4" data-testid="back-to-leases">
        <ArrowLeft className="w-4 h-4"/> Back to leases
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 md:p-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">Lease #{lease.lease_id?.slice(-8)}</div>
                <h1 className="font-display text-3xl font-bold mt-2 text-slate-950">{lease.tenant_email}</h1>
                <div className="text-sm text-slate-500 mt-1">Unit {lease.unit_id?.slice(-8)}</div>
              </div>
              <Pill tone={lease.status === "signed" ? "green" : lease.status === "active" ? "blue" : "amber"}>{lease.status || "draft"}</Pill>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <Detail icon={Calendar} label="Term">
                {formatDate(lease.start_date)} → {formatDate(lease.end_date)}
              </Detail>
              <Detail icon={DollarSign} label="Monthly rent">
                {formatCents(lease.monthly_rent)}
              </Detail>
              <Detail icon={DollarSign} label="Security deposit">
                {formatCents(lease.deposit || 0)}
              </Detail>
              <Detail icon={User} label="Created">
                {formatDate(lease.created_at)}
              </Detail>
            </div>

            {/* Inline correctness — RTA violations surface without navigating away. */}
            <AutoCorrectness
              className="mt-6"
              schema_hint="lease"
              province={lease.province}
              payload={{
                lease_id:    lease.lease_id,
                province:    lease.province,
                rent:        Math.round((lease.monthly_rent || 0) / 100),
                deposit:     Math.round((lease.deposit || 0) / 100),
                start_date:  lease.start_date,
                end_date:    lease.end_date,
                status:      lease.status,
              }}
            />

            {/* Auto — next best actions for this lease. */}
            <AutoChips
              className="mt-4"
              entity="lease"
              goal="move this lease to signed or renew ahead of expiry"
              context={{
                status:      lease.status,
                province:    lease.province,
                start_date:  lease.start_date,
                end_date:    lease.end_date,
                rent_cad:    Math.round((lease.monthly_rent || 0) / 100),
                tenant:      lease.tenant_email,
              }}
              onAction={(c) => toast.success(c.label, { description: c.reason })}
            />
          </Card>

          <Card className="p-6 md:p-8">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h2 className="font-display text-xl font-bold text-slate-900">Lease document</h2>
              {user?.role === "landlord" && (
                <Button variant={docText ? "secondary" : "primary"} onClick={() => setGenOpen(true)} data-testid="open-lease-generator">
                  <Sparkles className="w-4 h-4"/> {docText ? "Regenerate lease" : "Generate lease"}
                </Button>
              )}
            </div>
            {docText ? (
              <div className="bg-slate-50 rounded-lg p-5 max-h-[440px] overflow-y-auto whitespace-pre-wrap text-sm text-slate-700 leading-relaxed border border-slate-200" data-testid="lease-document-text">
                {docText}
              </div>
            ) : (
              <div className="text-sm text-slate-500 py-8 text-center bg-slate-50 rounded-lg border border-dashed border-slate-200">
                No document yet. {user?.role === "landlord" && "Click \"Generate lease\" to create one."}
              </div>
            )}
          </Card>
        </div>

        <div>
          <Card className="p-6 sticky top-6">
            <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Signatures</h3>
            <div className="space-y-3">
              <SignatureRow label="Landlord" signed={lease.signed_by_landlord} when={lease.signed_by_landlord_at}/>
              <SignatureRow label="Tenant" signed={lease.signed_by_tenant} when={lease.signed_by_tenant_at}/>
            </div>
            <div className="mt-6 border-t border-slate-100 pt-5">
              <Button onClick={sign} disabled={signing} className="w-full" data-testid="sign-lease-button">
                <FileSignature className="w-4 h-4"/> {signing ? "Signing…" : `Sign as ${user?.role}`}
              </Button>
              <p className="text-xs text-slate-500 mt-3 leading-relaxed">By signing, you agree to the terms of this lease document as a {user?.role}.</p>
            </div>
          </Card>
        </div>
      </div>

      <Modal open={genOpen} onClose={() => setGenOpen(false)} title="Lease Generator" testid="lease-generator-modal">
        <LeaseGenerator
          lease={lease}
          onCancel={() => setGenOpen(false)}
          onGenerated={(newDoc) => { setDoc(newDoc); setGenOpen(false); }}
        />
      </Modal>
    </div>
  );
}

function Detail({ icon: Icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider font-semibold">
        <Icon className="w-3.5 h-3.5"/> {label}
      </div>
      <div className="mt-1.5 text-base font-semibold text-slate-900">{children}</div>
    </div>
  );
}

function SignatureRow({ label, signed, when }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
      <div>
        <div className="font-semibold text-slate-900 text-sm">{label}</div>
        <div className="text-xs text-slate-500">{signed ? formatDate(when) : "Pending"}</div>
      </div>
      <Pill tone={signed ? "green" : "amber"}>{signed ? "Signed" : "Awaiting"}</Pill>
    </div>
  );
}
